// src/utils/caseIdUtils.js
import { Timestamp } from 'firebase/firestore';

/**
 * Utility functions for generating consistent case IDs
 * Format: YYYYMMDDXXXXXXXX (date prefix + 4-digit UID hash + 4-digit NIE hash)
 */

/**
 * Extract and format date prefix from a case record
 * @param {Object} record - Case record with registeredDate field
 * @returns {string} - Date in YYYYMMDD format
 */
export const getDatePrefix = (record) => {
    let date = null;
    
    if (record && record.registeredDate) {
        // Handle Firestore Timestamp
        if (record.registeredDate instanceof Timestamp || 
            (typeof record.registeredDate === 'object' && record.registeredDate.seconds)) {
            date = new Date(record.registeredDate.seconds * 1000);
        }
        // Handle Firestore toDate() method
        else if (typeof record.registeredDate === 'object' && typeof record.registeredDate.toDate === 'function') {
            date = record.registeredDate.toDate();
        }
        // Handle ISO string
        else if (typeof record.registeredDate === 'string') {
            date = new Date(record.registeredDate);
        }
        // Handle numeric timestamp
        else if (typeof record.registeredDate === 'number') {
            date = new Date(record.registeredDate);
        }
        // Already a Date object
        else if (record.registeredDate instanceof Date) {
            date = record.registeredDate;
        }
    }
    
    // Fallback to a default date if no valid date found
    if (!date || isNaN(date.getTime())) {
        date = new Date('2024-01-01'); // Default fallback date
    }
    
    // Format as YYYYMMDD
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}${month}${day}`;
};

/**
 * Generate a consistent 4-digit hash from a string
 * @param {string} str - Input string to hash
 * @returns {string} - 4-digit hash with leading zeros
 */
export const generateHash = (str) => {
    if (!str) return '0000';

    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32-bit integer
    }
    // Use absolute value and ensure 4 digits with leading zeros
    return Math.abs(hash).toString().padStart(4, '0').slice(-4);
};

/**
 * Generate a consistent case ID for a record
 * @param {Object} record - Case record with id, dni, and registeredDate
 * @returns {string} - Case ID in format YYYYMMDDXXXXXXXX or stored caseNumber
 */
export const generateCaseId = (record) => {
    if (!record) return 'N/A';

    // Use stored caseNumber if available
    if (record.caseNumber) {
        return record.caseNumber;
    }

    // Generate consistent ID for legacy cases
    if (record.id) {
        const datePrefix = getDatePrefix(record);
        const uidHash = generateHash(record.id);
        const nieHash = generateHash(record.dni || record.nie || '');
        return `${datePrefix}${uidHash}${nieHash}`;
    }

    return 'N/A';
};

/**
 * Alternative: Generate case ID with longer combined hash for better collision resistance
 * @param {Object} record - Case record with id, dni, and registeredDate
 * @returns {string} - Case ID in format YYYYMMDDXXXXXXXX
 */
export const generateRobustCaseId = (record) => {
    if (!record) return 'N/A';

    // Use stored caseNumber if available
    if (record.caseNumber) {
        return record.caseNumber;
    }

    // Generate consistent ID for legacy cases
    if (record.id) {
        const datePrefix = getDatePrefix(record);

        // Create a combined string and hash it for better distribution
        const combinedString = `${record.id}|${record.dni || record.nie || ''}|${record.firstName || ''}|${record.lastName || ''}`;
        const robustHash = generateRobustHash(combinedString);

        return `${datePrefix}${robustHash}`;
    }

    return 'N/A';
};

/**
 * Generate a more robust 8-digit hash from combined data
 * @param {string} str - Combined string to hash
 * @returns {string} - 8-digit hash with better distribution
 */
export const generateRobustHash = (str) => {
    if (!str) return '00000000';

    // Use a more sophisticated hash algorithm (FNV-1a variant)
    let hash = 2166136261; // FNV offset basis

    for (let i = 0; i < str.length; i++) {
        hash ^= str.charCodeAt(i);
        hash *= 16777619; // FNV prime
        hash = hash >>> 0; // Convert to unsigned 32-bit
    }

    // Take modulo to get 8 digits and pad with zeros
    return (hash % 100000000).toString().padStart(8, '0');
};

/**
 * Get today's date prefix for new case creation
 * @returns {string} - Today's date in YYYYMMDD format
 */
export const getTodayDatePrefix = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}${month}${day}`;
};
